import type { AuthContextProps } from "react-oidc-context";
import { purgePersistedStore } from "../store";
import { clearImportDataFromSession } from "./classImporterHelpers";

type ClearLocalSessionOptions = {
  preserveOidcStorage?: boolean;
};

const OIDC_STORAGE_PREFIX: string = "oidc.";

export const logout = async (
  auth: Pick<AuthContextProps, "user" | "signoutRedirect">,
): Promise<void> => {
  const idTokenHint = auth.user?.id_token;
  await clearLocalSession({ preserveOidcStorage: true });
  await auth.signoutRedirect({ id_token_hint: idTokenHint });
};

const attempt = async (fn: () => Promise<unknown>): Promise<unknown> => {
  try {
    await fn();
    return null;
  } catch (error) {
    return error;
  }
};

export const frontChannelLogout = async (
  auth: Pick<AuthContextProps, "removeUser">,
): Promise<void> => {
  // Run separately, not one try/catch around both, so a removeUser
  // failure can't stop clearLocalSession from running
  const removeUserError = await attempt(() => auth.removeUser());
  const cleanupError = await attempt(() => clearLocalSession());

  const error = removeUserError ?? cleanupError;
  if (error) throw error;
};

const clearLocalSession = async ({
  preserveOidcStorage = false,
}: ClearLocalSessionOptions = {}): Promise<void> => {
  await clearApiCache();
  clearLocalStorage({ preserveOidcStorage });
  clearImportDataFromSession();
  await purgePersistedStore();
};

const clearLocalStorage = ({
  preserveOidcStorage,
}: ClearLocalSessionOptions): void => {
  if (!preserveOidcStorage) {
    localStorage.clear();
    return;
  }

  const preservedEntries = Object.keys(localStorage)
    .filter((key) => key.startsWith(OIDC_STORAGE_PREFIX))
    .map((key) => [key, localStorage.getItem(key)]);

  localStorage.clear();

  preservedEntries.forEach(([key, value]) => {
    if (value !== null) {
      localStorage.setItem(key, value);
    }
  });
};

// Remove the service worker's cached project responses so a logged-out user's private project responses can't be served to a later user on a shared device while offline
// Keep this cache name in sync with "api-v1" in src/service-worker.js
const clearApiCache = async (): Promise<void> => {
  if (typeof caches === "undefined") return;
  try {
    await caches.delete("api-v1");
  } catch {
    // Don't let cache removal block logout
  }
};
